import React, { useEffect, useRef } from 'react';
import { claimAudioChannel, releaseAudioChannel } from '../utils/audioChannel';

/**
 * EjectionAnimation - Shows pilot ejecting from shot-down aircraft
 * Plays country-specific 5-second ejection video with layered sound effects
 */
export default function EjectionAnimation({ 
  sourceCountry, 
  bomberType,
  onComplete 
}) {
  const videoRef = useRef(null);
  
  // 🐛 BUG FIX: Keep onComplete in a ref so the main useEffect doesn't
  // re-fire every time the parent re-renders with a new inline arrow
  // function. Previously this caused the 10s timer + audio to restart
  // on every parent render, creating an infinite ejection loop that
  // only ESC + Save & Exit could break.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Map country + bomber type to ejection video
  const EJECTION_VIDEOS = {
    'USA': {
      'b1': 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ra64sgqy_Eject%20B1%20Bomber%20USA.mp4',
      'stealth': 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/scm9m37o_Eject%20Stealth%20Bomber%20USA.mp4'  // ✅ NEW! Dedicated stealth bomber video
    },
    'RUS': {
      'su34': 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ja8vlr7n_Eject%20Russian%20H-6%20Bomber.mp4',  // ✅ Updated
      'h6': 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ja8vlr7n_Eject%20Russian%20H-6%20Bomber.mp4'
    },
    'CHN': {
      'h6': 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/8v3kp2or_Eject%20China%20H-6.mp4'  // ✅ CHINA COMPLETE!
    },
    'EU': {
      'f35': 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ulgcggkp_Eject%20EU%20F-35%20Lightning%20II%20Bomber.mp4',
      'f16': 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ulgcggkp_Eject%20EU%20F-35%20Lightning%20II%20Bomber.mp4'  // Use F-35 for F-16 too
    },
    'NK': {
      'il28': 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/kanlid2j_Eject%20North%20Korea%20Ilyushin%20Il-28%20Bomber.mp4',
      'su25': 'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/thr2y7pd_Eject%20North%20Korea%20Sukhoi%20Su-25%20bomber.mp4'
    }
  };

  // ✅ Sound effects pool - Randomly selected during ejection
  const EJECTION_SOUND_EFFECTS = [
    'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/9mqn2wls_AEROJet-Realistic_sounds_of_-Elevenlabs.mp3',  // Aero jet sound
    'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/1seoci94_WEAPMisc-anti_aircraft_fire-Elevenlabs%20%282%29.mp3',  // Anti-aircraft fire 1
    'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/gov1ptdn_WEAPMisc-anti_aircraft_fire-Elevenlabs%20%281%29.mp3',  // Anti-aircraft fire 2
    'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/a06k29md_WEAPMisc-anti_aircraft_fire-Elevenlabs.mp3',  // Anti-aircraft fire 3
    'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/o9rl9gru_EXPLReal-Brief_and_distant_ex-Elevenlabs.mp3',  // Explosion sound 1
    'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/gsejkwaj_EXPLReal-Brief_and_distant_ex-Elevenlabs.mp3',  // Explosion sound 2 (NEW!)
    'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ve2xk53p_AEROJet-Three_F-35_fighter_j-Elevenlabs.mp3',  // F-35 fighter jet sounds (NEW!)
    'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/anulr9u0_AEROJet-fighter_jet_ejection-Elevenlabs.mp3'  // Fighter jet ejection sound (NEW!)
  ];

  // ✅ Background soundtracks - Randomly selected (plays throughout ejection)
  const EJECTION_SOUNDTRACKS = [
    'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/ie7homws_Eject%201%20Burn%20Through%20The%20Sky.mp3',  // Burn Through The Sky 1
    'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/53ifkyvw_Eject%202%20Burn%20Through%20Sky.mp3',  // Burn Through Sky 2
    'https://customer-assets.emergentagent.com/job_browser-game-hub-3/artifacts/n1dpagdl_Eject%205%20Burn%20trhough%20Sky%202.mp3'  // Burn Through Sky 5
  ];

  // 🎤 PILOT SCREAMS - Randomly selected (plays during ejection for dramatic effect)
  const PILOT_SCREAMS = [
    '/assets/audio/eject-scream-wilhelm.wav',      // Classic Wilhelm scream
    '/assets/audio/eject-scream-agony.mp3',        // Male scream of agony
    '/assets/audio/eject-scream-terrifying.mp3',   // Terrifying male scream
    '/assets/audio/eject-scream-ambient.mp3'       // Dark horror ambient scream
  ];

  // Get ejection video for this country/bomber
  const ejectionVideo = EJECTION_VIDEOS[sourceCountry]?.[bomberType];

  useEffect(() => {
    // If no video available, skip ejection animation immediately
    if (!ejectionVideo) {
      console.error(`❌ No ejection video for ${sourceCountry} ${bomberType} - calling onComplete`);
      onCompleteRef.current();
      return;
    }

    console.log(`🪂 EJECTION SEQUENCE: ${sourceCountry} ${bomberType} pilot ejecting!`);
    console.log(`🎬 Playing video: ${ejectionVideo}`);

    // 🔈 Claim the audio channel explicitly so iOS WebKit hands us the slot.
    // Without this, ejection mounts as a child of AirStrikeAnimation and
    // its sounds silently fail on phones.
    claimAudioChannel();

    // ✅ PERFORMANCE FIX: Preload video to reduce buffering delay
    if (videoRef.current) {
      videoRef.current.load();  // Force immediate loading
      console.log('⚡ Video preloading initiated');
    }

    // Track all timeouts and audio elements for cleanup
    const timeouts = [];
    const soundEffects = [];
    let soundtrack = null;
    const soundtrack2 = null; // 🚫 No longer used — kept for cleanup ref symmetry
    let pilotScream = null;

    // 🔈 iOS WebKit can only juggle ~2 concurrent Audio instances reliably.
    // Previously we layered 7+ sources here (4 jet SFX + 2 soundtracks +
    // 1 scream) and they all silently failed on phones. Now we play
    // exactly ONE jet SFX + ONE pilot scream — the scream is the
    // dramatic centerpiece anyway.
    if (EJECTION_SOUND_EFFECTS.length > 0) {
      const randomSound = EJECTION_SOUND_EFFECTS[Math.floor(Math.random() * EJECTION_SOUND_EFFECTS.length)];
      const audio1 = new Audio(randomSound);
      audio1.volume = 0.7;
      audio1.play().catch(e => console.warn('Ejection jet SFX failed:', e));
      soundEffects.push(audio1);
      console.log(`🔊 Jet SFX: ${randomSound.split('/').pop()}`);
    }

    // 🎤 Pilot scream — single play, dramatic
    if (PILOT_SCREAMS.length > 0) {
      const randomScream = PILOT_SCREAMS[Math.floor(Math.random() * PILOT_SCREAMS.length)];
      pilotScream = new Audio(randomScream);
      pilotScream.volume = 0.9;
      pilotScream.play().catch(e => console.warn('Pilot scream failed:', e));
      console.log(`😱 Pilot screaming: ${randomScream.split('/').pop()}`);
    }

    // ✅ EXTENDED: Auto-complete after 10 seconds (was 5s - extended to give video time to load + play fully)
    // Videos take 1-2s to buffer, so we need more time than the raw video duration
    const completionTimer = setTimeout(() => {
      console.log('✅ Ejection animation complete - stopping all audio');
      soundEffects.forEach(sfx => {
        sfx.pause();
        sfx.currentTime = 0;
      });
      if (soundtrack) {
        soundtrack.pause();
        soundtrack.currentTime = 0;
      }
      if (soundtrack2) {
        soundtrack2.pause();
        soundtrack2.currentTime = 0;
      }
      if (pilotScream) {
        pilotScream.pause();
        pilotScream.currentTime = 0;
      }
      onCompleteRef.current();
    }, 10000);  // 10 seconds total (5s video + 5s buffer/viewing time)
    timeouts.push(completionTimer);

    // ✅ CLEANUP: Stop all audio and clear all timeouts when component unmounts
    return () => {
      console.log('🧹 CLEANUP: Ejection animation unmounting - clearing all timers and audio');
      timeouts.forEach(timeout => clearTimeout(timeout));
      soundEffects.forEach(sfx => {
        sfx.pause();
        sfx.currentTime = 0;
      });
      if (soundtrack) {
        soundtrack.pause();
        soundtrack.currentTime = 0;
      }
      if (soundtrack2) {
        soundtrack2.pause();
        soundtrack2.currentTime = 0;
      }
      if (pilotScream) {
        pilotScream.pause();
        pilotScream.currentTime = 0;
      }
      // 🔈 Release audio channel so background music can resume
      releaseAudioChannel();
    };
  }, [sourceCountry, bomberType]);
  // 🐛 BUG FIX: Removed `onComplete` and `ejectionVideo` from deps.
  // `onComplete` is captured via onCompleteRef (always latest) and
  // `ejectionVideo` is fully determined by sourceCountry+bomberType.
  // Including either caused the effect to re-run on every parent
  // re-render → infinite ejection animation loop.

  // If no video available, return null (skip ejection animation)
  if (!ejectionVideo) {
    console.error(`❌ CRITICAL: No ejection video configured for ${sourceCountry} ${bomberType}`);
    console.error(`Available videos:`, EJECTION_VIDEOS);
    return null;
  }

  console.log(`✅ Ejection video found: ${ejectionVideo}`);

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 animate-fadeIn"
      style={{ pointerEvents: 'none' }}
    >
      {/* Red danger spotlight effect */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at center, rgba(239, 68, 68, 0.3) 0%, rgba(239, 68, 68, 0.15) 30%, transparent 60%)',
        }}
      />

      {/* Ejection video - centered at native resolution */}
      <div className="relative z-10 animate-fadeIn">
        <video
          ref={videoRef}
          src={ejectionVideo}
          autoPlay
          muted={false}  // Play video audio
          playsInline
          preload="auto"  // ✅ Preload to reduce loading time
          loop={false}  // Don't loop
          className="object-contain drop-shadow-2xl"
          style={{
            filter: 'drop-shadow(0 0 40px rgba(239, 68, 68, 0.8))',
            maxWidth: '105vw',  // 150% size (1.5x of 70vw)
            maxHeight: '105vh', // 150% size (1.5x of 70vh)
          }}
          onLoadStart={() => console.log('🎬 Video loading started...')}
          onCanPlay={() => console.log('✅ Video ready to play')}
          onPlaying={() => console.log('▶️ Video playing')}
          onError={(e) => {
            console.error('❌ Ejection video failed to load:', e);
            onCompleteRef.current();  // Skip to next phase if video fails
          }}
        />
      </div>

      {/* Overlay text - Shrunk to fit in frame */}
      <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 text-center z-10">
        <div 
          className="text-3xl font-bold text-red-500 animate-pulse mb-3"
          style={{
            textShadow: '0 0 20px rgba(239, 68, 68, 0.8), 0 0 40px rgba(239, 68, 68, 0.5)',
            fontFamily: "'Orbitron', sans-serif"
          }}
        >
          🪂 PILOT EJECTING
        </div>
        <div className="text-lg text-white opacity-90">
          {sourceCountry} {bomberType.toUpperCase()} SHOT DOWN
        </div>
      </div>

      {/* Red warning flash */}
      <div 
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          animation: 'flash-red 0.5s ease-in-out 3',
          background: 'radial-gradient(circle, rgba(239,68,68,0.3) 0%, transparent 70%)'
        }}
      />

      <style jsx>{`
        @keyframes flash-red {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}
